(ns io.daskul.tutorial.webapp.views.calendar.kinds
  "#512 — how a calendar card presents what kind of session it is.

   A card used to be coloured by its PROGRAM name, matched against a hardcoded
   five-entry substring table (piano → brand, violin → teal, …). That told you
   nothing you couldn't read off the card, and any program outside the five
   collapsed to violet. The card now carries the one thing worth scanning for:
   whether there is a free trial, a make-up, or a paid student in the room.

   The API serves `:attendee-kinds` — a precedence-ordered vector of the
   DISTINCT kinds among the session's live attendees, `[]` when it has none.
   The card takes `first` for the colour and `(> (count …) 1)` for the mixed
   marker, so the two cannot disagree about the same session.

   Precedence is makeup > trial > paid, not paid-dominant. Paid is the silent
   majority of a working calendar; painting a mixed session with it buries the
   rare card an admin is actually looking for.

   Colour is never the only carrier: `kinds->tag` gives every non-paid card a
   text tag saying the same thing in words. Roughly 1 in 12 men has a
   colour-vision deficiency, and trial-vs-paid is exactly the binary that gets
   accidentally encoded in hue alone.

   Deliberately NOT in lib/webapp-ui/css/tokens/colors.css: that file belongs
   to the in-flight design-system series (#684 →) and this story is
   file-disjoint from it. Folding these into the token layer is a follow-up
   once that series lands."
  (:require
   [clojure.string :as str]))

;; The colour lives in the card's SURFACE, not its text. Every word on a card
;; — time, tag, program, names — is the theme's ordinary grey; the hue is the
;; background plus the 3px spine. Tinting the text too gave a card two voices
;; saying one thing, and made a tag and a student's name look like the same
;; kind of information when one is a category and the other is a person.
;;
;; So each kind carries a background, a hairline border, and a spine:
;;
;;   kind      spine on bg   card-text (theme gray-7) on bg
;;   makeup    4.86:1        7.26:1
;;   trial     4.59:1        7.25:1
;;   paid      5.07:1        6.83:1
;;   neutral   4.40:1        7.49:1
;;
;; :accent is a non-text indicator, so it only owes 3:1 (WCAG 1.4.11) — worth
;; measuring anyway, because the obvious mid-tone picks miss even that (the
;; ramp's own accent-6 #da7009 lands at 2.96:1 on the trial card). :border is
;; decorative hairline separation and carries no meaning on its own.
;;
;; The text column is the constraint the backgrounds have to satisfy now: one
;; grey has to clear the 4.5:1 AA floor on all four, rather than each kind
;; bringing a bespoke foreground that only works on its own card.
;;
;; trial and paid keep the backgrounds `widgets/fsm/colors.cljs` already
;; measured for state badges — :attention orange and :active purple — so the
;; calendar and the pipeline speak the same visual language: orange is the
;; thing wanting your attention, purple the steady state. Both moved onto the
;; Klei ramps in #798 and moved here in the same commit, because a card and
;; the badge for the same lead sitting one hue apart is the whole failure
;; mode. makeup is the one new hue; teal sits clear of both, and clear of the
;; red/green that read as error and success elsewhere in the app.
(def kind-palette
  {"makeup" {:bg "#ccfbf1" :border "#99f6e4" :accent "#0f766e"}
   "trial"  {:bg "#fdf1cc" :border "#fce18f" :accent "#b54e0c"}
   "paid"   {:bg "#efe7fe" :border "#e3d5fd" :accent "#7536e9"}})

(def neutral-colors
  "A session with no live attendees — an empty slot, not a kind. Also the
   landing spot for a kind the server grows that this build doesn't know."
  {:bg "#f5f5f4" :border "#e7e5e4" :accent "#78716c"})

(def card-text-color
  "What every word on a card is painted in — the theme token, so the calendar
   tracks the app's text colour instead of pinning its own copy of it.

   `card-text-reference` below is the value this resolves to today, kept for
   the contrast test. A unit test can't resolve a CSS variable, which is
   exactly the gap `e2e/tests/badge-contrast.spec.ts` covers by measuring what
   the browser actually computes."
  "var(--mantine-color-gray-7)")

(def card-text-reference
  "Mirror of the theme's gray-7, for asserting the backgrounds are light
   enough to carry it. Same convention as the tone pairs in badge-contrast.
   If the theme moves and this doesn't, the e2e probe is what notices."
  "#495057")

(defn kind->card-colors
  "{:bg :border :accent} for a session's dominant attendee kind.

   Total by construction: nil (no live attendees) and an unrecognised kind
   both land on the neutral pair. A calendar that threw — or rendered
   colourless — because the server added a fourth kind would be a worse
   failure than one grey card."
  [kind]
  (get kind-palette kind neutral-colors))

;; ============================================================================
;; The text tag — colour's non-colour carrier
;; ============================================================================

(def ^:private kind->label
  {"makeup" "Makeup"
   "trial"  "Trial"
   "paid"   "Paid"})

(defn kinds->tag
  "`{:text :label}` for the card's tag, or nil when the card wants none.

   Paid is the unlabelled baseline. Most of a working calendar is paid
   sessions; tagging every one of them turns the tag into wallpaper that
   carries no information, and leaves the eye no quiet background to pick the
   trials and make-ups out of. Empty sessions go untagged for the same reason
   — the absence of a tag is itself readable.

   A trailing `+` marks a mixed session. It can only ever land on a Trial or
   Makeup tag: paid sits last in the precedence order, so a card whose
   dominant kind is paid has nothing left to be mixed with. There is no case
   where the marker needs a tag that isn't there.

   :label spells out what the `+` stands for, and becomes the tag's tooltip —
   a bare `+` is a symbol you have to be taught."
  [kinds]
  (let [dominant (first kinds)
        mixed? (> (count kinds) 1)
        label (get kind->label dominant)]
    (when (and label (not= "paid" dominant))
      {:text (if mixed? (str label " +") label)
       :label (str/join " · " (keep kind->label kinds))})))
